import * as React from 'react';
import { cn } from '../../lib/utils';

/**
 * Input — Generated from Figma Nimbus v3 Design System
 * @figma-node 65:533
 * @figma-url https://www.figma.com/design/euN3FdxtNlCC8GiKC66YmG/Nimbus-v3--TBD-?node-id=65:533
 *
 * Variants: Default, File
 * Horizontal Layout: Yes, No
 * States: Default, Hover, Focus, Error, Error (Focus), Filled, Disabled
 *
 * All colors extracted from Figma API — not guessed.
 */

interface InputProps extends Omit<React.ComponentProps<'input'>, 'type'> {
  /** Label displayed above the input */
  label?: React.ReactNode;
  /** Description displayed below the input */
  description?: React.ReactNode;
  /** Whether the input is in an error state */
  error?: boolean;
  /** Left icon element */
  leftIcon?: React.ReactNode;
  /** Right icon element */
  rightIcon?: React.ReactNode;
  /** Whether to use horizontal layout (label beside input) */
  horizontal?: boolean;
  /** Input type — excludes "file" (use variant="file" instead) */
  type?: React.HTMLInputTypeAttribute;
}

function Input({
  className,
  label,
  description,
  error = false,
  disabled,
  leftIcon,
  rightIcon,
  horizontal = false,
  id,
  type = 'text',
  ...props
}: InputProps) {
  const generatedId = React.useId();
  const inputId = id || generatedId;
  const descriptionId = description ? `${inputId}-description` : undefined;

  const inputElement = (
    <input
      id={inputId}
      data-slot="input"
      type={type}
      disabled={disabled}
      aria-invalid={error || undefined}
      aria-describedby={descriptionId}
      className={cn(
        /*
         * Base — reset + full width, transparent bg (bg on wrapper)
         * Figma: fontSize=14, fontWeight=400, lineHeight=20
         */
        'w-full bg-transparent text-sm font-normal leading-5 outline-none',

        /*
         * Text color — Figma: filled text=#202020 (foreground)
         */
        'text-nimbus-foreground',

        /*
         * Placeholder — Figma: fill=#727272 (muted-foreground)
         */
        'placeholder:text-nimbus-muted-foreground',

        /*
         * Disabled
         */
        'disabled:cursor-not-allowed',

        /*
         * File input styling
         */
        'file:text-sm file:font-medium file:border-0 file:bg-transparent file:text-nimbus-foreground'
      )}
      {...props}
    />
  );

  const wrapperElement = (
    <div
      data-slot="input-wrapper"
      data-error={error || undefined}
      className={cn(
        /*
         * Input box base — Figma: fill=#ffffff, stroke=#cecece, radius=8,
         * height=36, padding=12/4, shadow 0 1 2 0 #202020/5%
         */
        'flex h-9 w-full items-center gap-1 rounded-lg border bg-white px-3 py-1 text-sm',
        'border-nimbus-input shadow-nimbus-xs',
        'transition-all',

        /*
         * Hover — Figma: stroke=#727272
         */
        'hover:border-nimbus-muted-foreground',

        /*
         * Focus-within — Figma: stroke=#1e5eed, ring 3px spread #a3a3a3/50
         */
        'focus-within:border-nimbus-focus focus-within:shadow-nimbus-focus-outline',

        /*
         * Error — Figma: stroke=#db2929
         */
        error && 'border-nimbus-destructive hover:border-nimbus-destructive',

        /*
         * Error + Focus — Figma: stroke=#db2929, ring 3px spread #dc2626/20
         */
        error &&
          'focus-within:border-nimbus-destructive focus-within:shadow-nimbus-focus-destructive',

        /*
         * Disabled — Figma: opacity=0.5
         */
        disabled && 'opacity-50 cursor-not-allowed',

        /*
         * Dark mode base — Figma: fill=rgba(255,255,255,0.045), stroke=rgba(255,255,255,0.15)
         */
        'dark:bg-white/[0.045] dark:border-white/[0.15]'

        /*
         * Dark mode hover — handled by nimbus-muted-foreground token
         */

        /*
         * Dark mode focus — handled by nimbus-focus-outline token
         */

        /*
         * Dark mode error — handled by nimbus-destructive token
         */
      )}
    >
      {leftIcon && (
        <span
          data-slot="input-icon-left"
          className="flex shrink-0 items-center text-nimbus-muted-foreground [&_svg]:size-4"
        >
          {leftIcon}
        </span>
      )}
      {inputElement}
      {rightIcon && (
        <span
          data-slot="input-icon-right"
          className="flex shrink-0 items-center text-nimbus-muted-foreground [&_svg]:size-4"
        >
          {rightIcon}
        </span>
      )}
    </div>
  );

  if (!label && !description) {
    return wrapperElement;
  }

  return (
    <div
      data-slot="input-field"
      className={cn(
        horizontal ? 'flex items-start gap-4' : 'flex flex-col gap-2',
        className
      )}
    >
      {label && (
        <label
          data-slot="input-label"
          htmlFor={inputId}
          className={cn(
            /*
             * Label — Figma: fontSize=14, fontWeight=500, fill=#202020
             */
            'text-sm font-medium leading-none text-nimbus-foreground',
            horizontal && 'pt-2.5 shrink-0',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          {label}
        </label>
      )}
      <div className="flex flex-col gap-2 w-full">
        {wrapperElement}
        {description && (
          <p
            id={descriptionId}
            data-slot="input-description"
            className={cn(
              /*
               * Description — Figma: fontSize=14, fill=#727272
               * Error: fill=#db2929
               */
              'text-sm leading-5',
              error ? 'text-nimbus-destructive' : 'text-nimbus-muted-foreground'
            )}
          >
            {description}
          </p>
        )}
      </div>
    </div>
  );
}

export { Input };
export type { InputProps };
